import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  type MotionValue,
  type PanInfo,
} from 'motion/react';
import { cn } from '@/lib/utils';
import { hapticTick } from '@/lib/haptics';

export type SwipeSide = 'left' | 'right' | null;

export interface SwipeActionConfig {
  key: string;
  label: string;
  onAction: () => void;
  icon?: React.ReactNode;
  tone?: 'default' | 'primary' | 'accent' | 'destructive' | 'success' | 'warning' | 'muted';
  disabled?: boolean;
  /**
   * How the row behaves when this action commits via a full swipe.
   * 'fly' slides the row offscreen (actions that remove the row: archive/delete);
   * 'settle' springs it back closed (actions that open UI or keep the row).
   * Only meaningful for the edge action of a rail.
   */
  commitStyle?: 'fly' | 'settle';
}

export interface SwipeableRowProps {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  /**
   * Actions revealed by swiping right, rendered left→right from the screen
   * edge. The FIRST action is the edge action: it expands and fires on a
   * full swipe.
   */
  leftActions?: SwipeActionConfig[];
  /**
   * Actions revealed by swiping left, rendered left→right ending at the
   * screen edge. The LAST action is the edge action: it expands and fires
   * on a full swipe.
   */
  rightActions?: SwipeActionConfig[];
  disabled?: boolean;
  onOpenChange?: (side: SwipeSide) => void;
}

export interface SwipeableRowHandle {
  close: () => void;
  openSide: () => SwipeSide;
}

// Rail actions render as circular icon discs (pill shape language) instead of
// full-height color slabs. The slot/expansion mechanics are unchanged: the
// "which action will fire" signal is the edge disc scaling up while the other
// discs fade at the commit point, not a color change keyed to drag distance.
// Tones follow the Pistachio/Plum palette — theme tokens where they exist,
// palette-harmonized oklch values (chart-token hue families) for the rest.
const toneClassMap: Record<NonNullable<SwipeActionConfig['tone']>, string> = {
  default: 'bg-[oklch(0.55_0.045_305)] text-white',
  primary: 'bg-primary text-primary-foreground',
  accent: 'bg-[oklch(0.55_0.12_260)] text-white',
  destructive: 'bg-destructive text-white',
  success: 'bg-[oklch(0.60_0.11_160)] text-white',
  warning: 'bg-[oklch(0.70_0.13_75)] text-white',
  muted: 'bg-[oklch(0.65_0.035_305)] text-white',
};

// Natural width of one rail button (icon + label), px.
const ACTION_WIDTH = 88;
// Extra travel beyond the fully-revealed rail before a slow drag commits.
const COMMIT_GAP = 56;
// Crossing back this many px under the commit point collapses the expansion.
const COMMIT_HYSTERESIS = 14;
// Seconds of velocity projected onto the release position (flick handling).
const FLICK_PROJECTION = 0.18;
// Fallback row width until the ResizeObserver reports one.
const FALLBACK_ROW_WIDTH = 360;

// Snappy critically-damped settle for open/close.
const SETTLE_SPRING = { type: 'spring' as const, stiffness: 480, damping: 44, mass: 1 };
// Fly-out after a committed swipe: softer than the settle so the departure
// reads as motion rather than a cut. `animate()` seeds it with the motion
// value's live velocity, so a fast release keeps its speed.
const FLY_SPRING = { type: 'spring' as const, stiffness: 300, damping: 34, mass: 1 };
// Edge-button expansion when crossing the commit point.
const EXPAND_SPRING = { type: 'spring' as const, stiffness: 550, damping: 42 };
// Edge-disc bump acknowledging the release itself.
const PULSE_SPRING = { type: 'spring' as const, stiffness: 700, damping: 30 };
// How long after a fly commit fires its action before a still-mounted row is
// treated as "not removed" and springs back into view.
const FLY_RESTORE_MS = 1500;

interface RailButtonProps {
  action: SwipeActionConfig;
  index: number;
  count: number;
  side: 'left' | 'right';
  isEdge: boolean;
  exposure: MotionValue<number>;
  edgeProgress: MotionValue<number>;
  commitPulse: MotionValue<number>;
  onTrigger: (action: SwipeActionConfig) => void;
  dragEndTimeRef: React.MutableRefObject<number>;
}

function RailButton({
  action,
  index,
  count,
  side,
  isEdge,
  exposure,
  edgeProgress,
  commitPulse,
  onTrigger,
  dragEndTimeRef,
}: RailButtonProps) {
  // Buttons tile the exposed area equally and fan out with the drag. On
  // commit the edge button expands from its slot to cover the full rail.
  const left = useTransform([exposure, edgeProgress], ([e, p]: number[]) => {
    const base = (e * index) / count;
    return isEdge ? base * (1 - p) : base;
  });
  const width = useTransform([exposure, edgeProgress], ([e, p]: number[]) => {
    const base = e / count;
    return isEdge ? base + (e - base) * p : base;
  });
  // Non-edge buttons fade as the edge action takes over the rail.
  const opacity = useTransform(edgeProgress, (p) => (isEdge ? 1 : 1 - p * 0.9));
  // Discs stay full size throughout the drag: they emerge as a stack from
  // under the row edge and fan apart as the slots widen (the slot centers do
  // the animating). Crossing the commit point bumps the edge disc up, and the
  // release itself adds a second, sharper bump on top.
  const discScale = useTransform([edgeProgress, commitPulse], ([p, pulse]: number[]) =>
    isEdge ? 1 + p * 0.12 + pulse * 0.22 : 1,
  );
  const discOpacity = useTransform(exposure, [8, 48], [0, 1]);

  const toneClass = toneClassMap[action.tone ?? 'default'];

  return (
    <motion.button
      type="button"
      aria-label={action.label}
      title={action.label}
      className={cn(
        'absolute inset-y-0 outline-none',
        isEdge ? 'z-10' : 'z-0',
        action.disabled && 'opacity-40',
      )}
      style={{ left, width, opacity }}
      onClick={(event) => {
        event.stopPropagation();
        // Ignore clicks during or immediately after a drag.
        if (Date.now() - dragEndTimeRef.current < 150) {
          return;
        }
        if (action.disabled) {
          return;
        }
        onTrigger(action);
      }}
      disabled={action.disabled}
    >
      <span
        className={cn(
          'pointer-events-none absolute inset-y-0 flex items-center justify-center',
          // Edge content pins to the screen edge (iOS behavior when the button
          // expands); the strip is exactly one natural button wide so it reads
          // as centered while the rail sits at its detent.
          isEdge ? (side === 'left' ? 'left-0' : 'right-0') : 'left-1/2 -translate-x-1/2',
        )}
        style={{ width: ACTION_WIDTH }}
      >
        <motion.span
          className={cn('flex size-14 items-center justify-center rounded-full shadow-sm', toneClass)}
          style={{ scale: discScale, opacity: discOpacity }}
        >
          {action.icon}
        </motion.span>
      </span>
    </motion.button>
  );
}

export const SwipeableRow = forwardRef<SwipeableRowHandle, SwipeableRowProps>(
  function SwipeableRow(
    {
      children,
      className,
      contentClassName,
      contentStyle,
      leftActions = [],
      rightActions = [],
      disabled = false,
      onOpenChange,
    },
    ref,
  ) {
    const x = useMotionValue(0);
    const leftEdgeProgress = useMotionValue(0);
    const rightEdgeProgress = useMotionValue(0);
    const leftCommitPulse = useMotionValue(0);
    const rightCommitPulse = useMotionValue(0);
    const [openSide, setOpenSide] = useState<SwipeSide>(null);
    const [rowWidth, setRowWidth] = useState(0);
    // True while a committed fly-out is in flight: the row is inert until the
    // action removes it (or the restore timer brings it back).
    const [isFlying, setIsFlying] = useState(false);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const mountedRef = useRef(true);
    const restoreTimerRef = useRef<number | null>(null);
    const dragEndTimeRef = useRef<number>(0);
    const dragDisplacementRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    // Whether each side is visually past its commit point (expansion shown).
    const commitVisualRef = useRef<{ left: boolean; right: boolean }>({ left: false, right: false });
    const rowWidthRef = useRef(0);

    const leftActionsRef = useRef(leftActions);
    leftActionsRef.current = leftActions;
    const rightActionsRef = useRef(rightActions);
    rightActionsRef.current = rightActions;

    const leftExposure = useTransform(x, (v) => Math.max(0, v));
    const rightExposure = useTransform(x, (v) => Math.max(0, -v));
    const leftRailOpacity = useTransform(x, [0, 2], [0, 1]);
    const rightRailOpacity = useTransform(x, [-2, 0], [1, 0]);

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        if (restoreTimerRef.current !== null) {
          window.clearTimeout(restoreTimerRef.current);
        }
      };
    }, []);

    useEffect(() => {
      const element = containerRef.current;
      if (!element) {
        return;
      }
      const measure = () => {
        rowWidthRef.current = element.getBoundingClientRect().width;
        setRowWidth((prev) => (prev !== rowWidthRef.current ? rowWidthRef.current : prev));
      };
      measure();
      if (typeof ResizeObserver === 'undefined') {
        return;
      }
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    }, []);

    const edgeActionFor = useCallback((side: 'left' | 'right') => {
      const actions = side === 'left' ? leftActionsRef.current : rightActionsRef.current;
      if (!actions.length) {
        return null;
      }
      return side === 'left' ? actions[0] : actions[actions.length - 1];
    }, []);

    const detentFor = useCallback((side: 'left' | 'right') => {
      const actions = side === 'left' ? leftActionsRef.current : rightActionsRef.current;
      return actions.length * ACTION_WIDTH;
    }, []);

    const commitPointFor = useCallback(
      (side: 'left' | 'right') => {
        const width = rowWidthRef.current || FALLBACK_ROW_WIDTH;
        const detent = detentFor(side);
        // Never let the commit point sit at/inside the detent (possible on very
        // narrow rows) — resting open must not arm the edge action.
        return Math.max(Math.min(Math.max(detent + COMMIT_GAP, width * 0.55), width * 0.9), detent + 40);
      },
      [detentFor],
    );

    const settleTo = useCallback(
      (targetX: number, onComplete?: () => void) => {
        animate(x, targetX, { ...SETTLE_SPRING, onComplete });
      },
      [x],
    );

    const setOpenWithAnimation = useCallback(
      (side: SwipeSide) => {
        const target = side === 'left' ? detentFor('left') : side === 'right' ? -detentFor('right') : 0;
        setOpenSide(side);
        settleTo(target);
        onOpenChange?.(side);
      },
      [detentFor, onOpenChange, settleTo],
    );

    useImperativeHandle(
      ref,
      () => ({
        close() {
          setOpenWithAnimation(null);
        },
        openSide() {
          return openSide;
        },
      }),
      [openSide, setOpenWithAnimation],
    );

    // Fires a rail action from a tap while the rail sits at its detent.
    const triggerAction = useCallback(
      (action: SwipeActionConfig) => {
        hapticTick(10);
        setOpenWithAnimation(null);
        action.onAction();
      },
      [setOpenWithAnimation],
    );

    // Fires the edge action from a full swipe past the commit point.
    const commitEdge = useCallback(
      (side: 'left' | 'right') => {
        const edge = edgeActionFor(side);
        if (!edge || edge.disabled) {
          setOpenWithAnimation(side);
          return;
        }
        hapticTick(20);
        setOpenSide(null);
        onOpenChange?.(null);
        const edgeProgress = side === 'left' ? leftEdgeProgress : rightEdgeProgress;
        const commitPulse = side === 'left' ? leftCommitPulse : rightCommitPulse;
        // A flick can commit before the drag crossed the visual commit point —
        // expand the edge button now so the fly-out reads as "this action won".
        if (!commitVisualRef.current[side]) {
          commitVisualRef.current[side] = true;
          animate(edgeProgress, 1, EXPAND_SPRING);
        }
        // Acknowledge the release itself: a sharp bump on the edge disc.
        commitPulse.jump(0);
        animate(commitPulse, 1, PULSE_SPRING);

        if (edge.commitStyle !== 'fly') {
          edge.onAction();
          settleTo(0, () => {
            animate(commitPulse, 0, PULSE_SPRING);
          });
          return;
        }

        // Fly: the row leaves the screen FIRST, then the action fires. Actions
        // like archive/delete remove the row from the store synchronously, so
        // firing them before the animation unmounts the row on the next render
        // and the fly-out never gets a frame. The edge disc stays pinned to
        // the screen edge as the row departs, then the list closes up.
        setIsFlying(true);
        const target = (side === 'left' ? 1 : -1) * Math.max(rowWidthRef.current, FALLBACK_ROW_WIDTH);
        animate(x, target, {
          ...FLY_SPRING,
          onComplete: () => {
            if (!mountedRef.current) {
              return;
            }
            edge.onAction();
            // The row normally unmounts within a render. If it is still here
            // after a beat (removal is deferred until a request resolves and
            // failed, or the action was a no-op), bring it back visibly
            // rather than leaving a blank slot.
            restoreTimerRef.current = window.setTimeout(() => {
              restoreTimerRef.current = null;
              if (!mountedRef.current) {
                return;
              }
              commitVisualRef.current = { left: false, right: false };
              animate(leftEdgeProgress, 0, EXPAND_SPRING);
              animate(rightEdgeProgress, 0, EXPAND_SPRING);
              animate(commitPulse, 0, PULSE_SPRING);
              settleTo(0, () => setIsFlying(false));
            }, FLY_RESTORE_MS);
          },
        });
      },
      [
        edgeActionFor,
        leftCommitPulse,
        leftEdgeProgress,
        onOpenChange,
        rightCommitPulse,
        rightEdgeProgress,
        setOpenWithAnimation,
        settleTo,
        x,
      ],
    );

    // Track the commit-point crossing to drive the edge expansion + haptic tick.
    useEffect(() => {
      const unsubscribe = x.on('change', (value) => {
        (['left', 'right'] as const).forEach((side) => {
          const exposure = side === 'left' ? Math.max(0, value) : Math.max(0, -value);
          const edge = edgeActionFor(side);
          const eligible = Boolean(edge && !edge.disabled);
          const progress = side === 'left' ? leftEdgeProgress : rightEdgeProgress;
          const committed = commitVisualRef.current[side];
          const commitAt = commitPointFor(side);
          if (!committed && eligible && exposure >= commitAt) {
            commitVisualRef.current[side] = true;
            hapticTick(12);
            animate(progress, 1, EXPAND_SPRING);
          } else if (committed && exposure < commitAt - COMMIT_HYSTERESIS) {
            commitVisualRef.current[side] = false;
            hapticTick(6);
            animate(progress, 0, EXPAND_SPRING);
          }
        });
      });
      return unsubscribe;
    }, [commitPointFor, edgeActionFor, leftEdgeProgress, rightEdgeProgress, x]);

    const handleDrag = useCallback((_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      dragDisplacementRef.current = { x: info.offset.x, y: info.offset.y };
    }, []);

    const handleDragEnd = useCallback(
      (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
        dragEndTimeRef.current = Date.now();
        const currentX = x.get();
        const { x: dx, y: dy } = dragDisplacementRef.current;
        dragDisplacementRef.current = { x: 0, y: 0 };

        const absX = Math.abs(dx);
        const absY = Math.abs(dy);
        const isHorizontalGesture = absX > absY * 1.5 || absX > 30;

        const side: 'left' | 'right' | null = currentX > 0 ? 'left' : currentX < 0 ? 'right' : null;
        const actions = side === 'left' ? leftActionsRef.current : side === 'right' ? rightActionsRef.current : [];
        if (!isHorizontalGesture || !side || !actions.length) {
          setOpenWithAnimation(null);
          return;
        }

        const exposure = Math.abs(currentX);
        const detent = detentFor(side);
        const commitAt = commitPointFor(side);
        const edge = edgeActionFor(side);
        const canCommit = Boolean(edge && !edge.disabled);

        // Project the flick: exposure the gesture "wants" to reach. A hard
        // flick commits the edge action without dragging the full distance —
        // but only once real travel passed half the rail, so a casual flick
        // snaps the rail open instead of firing archive/delete (B14).
        const towardOpenVelocity = side === 'left' ? info.velocity.x : -info.velocity.x;
        const projectedExposure = exposure + towardOpenVelocity * FLICK_PROJECTION;

        if (canCommit && (exposure >= commitAt || (projectedExposure >= commitAt && exposure >= detent * 0.5))) {
          commitEdge(side);
          return;
        }
        if (projectedExposure >= detent * 0.5) {
          setOpenWithAnimation(side);
          return;
        }
        setOpenWithAnimation(null);
      },
      [commitEdge, commitPointFor, detentFor, edgeActionFor, setOpenWithAnimation, x],
    );

    const closeOnOutsideTouch = useCallback(
      (event: TouchEvent) => {
        if (openSide === null) {
          return;
        }
        const container = containerRef.current;
        if (!container) {
          return;
        }
        if (!container.contains(event.target as Node)) {
          setOpenWithAnimation(null);
        }
      },
      [openSide, setOpenWithAnimation],
    );

    useEffect(() => {
      if (openSide === null) {
        return;
      }
      document.addEventListener('touchstart', closeOnOutsideTouch);
      return () => {
        document.removeEventListener('touchstart', closeOnOutsideTouch);
      };
    }, [closeOnOutsideTouch, openSide]);

    const maxDrag = rowWidth || FALLBACK_ROW_WIDTH;

    const renderRail = (side: 'left' | 'right') => {
      const actions = side === 'left' ? leftActions : rightActions;
      if (!actions.length) {
        return null;
      }
      const exposure = side === 'left' ? leftExposure : rightExposure;
      const edgeProgress = side === 'left' ? leftEdgeProgress : rightEdgeProgress;
      const commitPulse = side === 'left' ? leftCommitPulse : rightCommitPulse;
      const railOpacity = side === 'left' ? leftRailOpacity : rightRailOpacity;
      const isOpen = openSide === side;
      return (
        <motion.div
          className={cn(
            // Touch screens swipe at every width (the Duo's inner screen is md+);
            // only mouse/trackpad layouts use the hover overlay instead.
            'absolute inset-y-0 overflow-hidden rounded-xl md:hover-device:hidden',
            side === 'left' ? 'left-0' : 'right-0',
          )}
          style={{
            width: exposure,
            opacity: railOpacity,
            pointerEvents: isOpen ? 'auto' : 'none',
          }}
          aria-hidden={!isOpen}
        >
          {actions.map((action, index) => (
            <RailButton
              key={action.key}
              action={action}
              index={index}
              count={actions.length}
              side={side}
              isEdge={side === 'left' ? index === 0 : index === actions.length - 1}
              exposure={exposure}
              edgeProgress={edgeProgress}
              commitPulse={commitPulse}
              onTrigger={triggerAction}
              dragEndTimeRef={dragEndTimeRef}
            />
          ))}
        </motion.div>
      );
    };

    return (
      <div ref={containerRef} className={cn('relative touch-pan-y', className)}>
        {renderRail('left')}
        <motion.div
          className={cn(
            'relative will-change-transform',
            isFlying && 'pointer-events-none',
            contentClassName,
          )}
          style={{
            x,
            ...contentStyle,
          }}
          drag={disabled || isFlying ? false : 'x'}
          dragConstraints={{
            left: rightActions.length ? -maxDrag : 0,
            right: leftActions.length ? maxDrag : 0,
          }}
          dragElastic={0.05}
          dragMomentum={false}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          whileTap={{ scale: 0.995 }}
        >
          {children}
        </motion.div>
        {renderRail('right')}
      </div>
    );
  },
);
